import {
  Alert,
  DashboardTimeZone,
  datePresetOptions,
  FilterControl,
  FilterControlType,
  intlZoneOf,
  Page,
  ReportGroupPanel,
  SimpleSelect,
  Skeleton,
  timeFor,
  type AnalyticsSource,
  type DashboardZone,
} from 'corso-admin-ui';
import { analyticsCatalog } from 'corso-types';
import { formatter } from 'corso-web-lib';
import { Navigate, useLocation } from 'react-router-dom';
import { match } from 'ts-pattern';
import { useEnabledClaimType } from '~/hooks/useEnabledClaimType';
import useIsCorsoAdmin from '~/hooks/useIsCorsoAdmin';
import { useStoreId, useStoreUser } from '~/providers/StoreUserProvider';
import { organizationSource, storeSource } from './analyticsSources';
import { useDashboardParams } from './hooks/useDashboardParams';
import { useOrganizationCurrencies } from './hooks/useOrganizationCurrencies';
import { offeredReportGroups, reportGroups } from './reports/groups';
import type { ReportGroup } from './reports/types';

type ZoneNameStyle = 'short' | 'longGeneric';

const zoneName = (zone: DashboardZone, style: ZoneNameStyle) =>
  new Intl.DateTimeFormat(undefined, {
    timeZoneName: style,
    timeZone: intlZoneOf(zone),
  })
    .formatToParts(new Date())
    .find(({ type }) => type === 'timeZoneName')?.value ?? 'Local time';

const zoneLabel = (zone: DashboardZone, style: ZoneNameStyle) =>
  match(zone)
    .with(
      { key: DashboardTimeZone.store },
      (store) => `Store time (${zoneName(store, style)})`,
    )
    .with(
      { key: DashboardTimeZone.local },
      (local) => `Local time (${zoneName(local, style)})`,
    )
    .exhaustive();

export const DashboardPage = () => {
  const params = useDashboardParams();
  const {
    date,
    setDate,
    timeZone,
    timeZones,
    setTimeZone,
    organizations,
    organization,
    setOrganization,
    currency: chosenCurrency,
    setCurrency,
    group: chosen,
  } = params;

  const { search } = useLocation();
  const storeId = useStoreId();
  const { store } = useStoreUser();
  const time = timeFor(date, timeZone);
  const enabled = useEnabledClaimType();
  const isCorsoAdmin = useIsCorsoAdmin();
  const currencies = useOrganizationCurrencies(organization?.organizationId);
  const isError = enabled.isError || currencies.isError;
  const source = match(organization)
    .returnType<AnalyticsSource | undefined>()
    .with(null, () => storeSource(storeId, store.currencyCode))
    .otherwise(({ organizationId }) => {
      const offered = currencies.data ?? [];
      const currency =
        offered.find((code) => code === chosenCurrency) ?? offered[0];
      return currency === undefined
        ? undefined
        : organizationSource(organizationId, currency);
    });
  const groups = offeredReportGroups(
    enabled,
    organization !== null,
    isCorsoAdmin,
  );
  const activeGroup = groups?.find(({ key }) => key === chosen) ?? groups?.[0];

  // a link to no dashboard, or one this store isn't offered, opens the first it is
  if (activeGroup && activeGroup.key !== chosen) {
    return (
      <Navigate to={{ pathname: `../${activeGroup.key}`, search }} replace />
    );
  }

  return (
    <Page
      title={activeGroup?.label ?? 'Dashboards'}
      size="fullWidth"
      overrideActions={
        activeGroup && (
          <>
            <SimpleSelect
              label="Time zone"
              labelVisuallyHidden
              options={timeZones.map((zone) => ({
                value: zone.key,
                label: zoneLabel(zone, 'longGeneric'),
              }))}
              value={timeZone.key}
              onChange={setTimeZone}
            />
            {organizations.length > 0 && (
              <SimpleSelect
                label="View"
                labelVisuallyHidden
                options={[
                  { value: 'store', label: store.name },
                  ...organizations.map((option) => ({
                    value: `${option.organizationId}`,
                    label: option.organizationName,
                  })),
                ]}
                value={
                  organization ? `${organization.organizationId}` : 'store'
                }
                onChange={(value) =>
                  setOrganization(value === 'store' ? null : Number(value))
                }
              />
            )}
            {organization && source && (currencies.data?.length ?? 0) > 1 && (
              <SimpleSelect
                label="Currency"
                labelVisuallyHidden
                options={(currencies.data ?? []).map((code) => ({
                  value: code,
                  label: code,
                }))}
                value={source.currency}
                onChange={setCurrency}
              />
            )}
            <FilterControl
              filter={{
                id: 'date',
                type: FilterControlType.date,
                label: 'Date',
                verb: 'is',
                options: datePresetOptions,
                value: date,
              }}
              onChange={({ value }) => setDate(value)}
            />
          </>
        )
      }
    >
      {(groups === undefined || source === undefined) && !isError && (
        <Skeleton.Rectangle height="360px" width="100%" />
      )}
      {isError && (
        <Alert
          variant="critical"
          heading="Failed to load dashboards. Please try again later."
        />
      )}
      {groups && !activeGroup && (
        <Alert
          variant="info"
          heading={`Turn on ${formatter.list.or(reportGroups.filter((group: ReportGroup) => !group.isCorsoAdminOnly).map(({ label }) => label.toLowerCase()))} to see their dashboards.`}
        />
      )}
      {activeGroup &&
        source &&
        match(time)
          .with(null, () => (
            <Alert
              variant="info"
              heading={`Choose a start date no more than ${analyticsCatalog.limits.maxRangeDays} days before the end date.`}
            />
          ))
          .otherwise(() => (
            <ReportGroupPanel
              key={activeGroup.key}
              source={source}
              group={activeGroup}
              date={date}
              view={params}
            />
          ))}
    </Page>
  );
};
